import { createContext, useContext, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Link, useSearchParams } from "react-router";

import { Bento } from "@/components/ui/box";
import type { Box, FactBox, FactOwner, ListBox, Read, Result, TaskBox } from "@/components/ui/box.schema";
import { VisitHost } from "@/components/visit/VisitHost";
import type { VisitLevel } from "@/lib/visit/address";
import type { VisitResolution, VisitTarget } from "@/lib/visit/registry";
import type { TenantSlug } from "@/lib/theme-presets";
import { cn } from "@/lib/utils";

import { PEOPLE, READ_AT, VISIT_STATES, type InvitationState, type ResendOutcome, type SpecimenPerson, type TenantPeople, type VisitPreset, type VisitState } from "./fixtures";

/**
 * The visit host's specimen (V): the People drawing's register and its visit
 * states, drawn by the real host and the kit. `/design-system?visits=<state>`
 * draws one state alone, in the tenant the hostname chose; the visit itself is
 * the state's own `?visit=`, so reload, Back and a pasted link behave as they
 * do on any page.
 */

interface Model {
  invitations: Record<string, InvitationState>;
  resend: ResendOutcome;
  accessFailed: boolean;
  marksFailed: boolean;
  expanded: string[];
  filter: "all" | "no-account";
  inviteDraft: boolean;
  adding: boolean;
  /** What the next Send returns (`&outcome=`), as the Instructor specimen's picker does. */
  next: Exclude<ResendOutcome, "none">;
}

interface Store {
  data: TenantPeople;
  model: Model;
  apply: Dispatch<SetStateAction<Model>>;
}

const SpecimenStore = createContext<Store | null>(null);

const IMAM: FactOwner = { system: "imam" };
const SENT = { word: "Invitation sent", class: "quiet" } as const;
/** A visited box the address didn't name rests as one line with Open (O10). */
const OPEN = { does: "open", verb: "Open" } as const;
const first = (name: string) => name.split(" ")[0];
const short = (course: string) => course.split(" · ")[1] ?? course;
const ready = <T,>(value: T): Read<T> => ({ status: "ready", value, readAt: READ_AT });
const box = <T,>(spec: FactBox<T> | ListBox<T, unknown> | TaskBox<T>) => spec as unknown as Box;

function modelOf(data: TenantPeople, preset: VisitPreset, next: Model["next"]): Model {
  const invitations = Object.fromEntries(data.people.map((person) => [person.id, person.invitation]));
  if (preset.resent || preset.resend === "confirmed") invitations.p2 = SENT;
  return {
    invitations,
    resend: preset.resend ?? "none",
    accessFailed: preset.accessFailed ?? false,
    marksFailed: preset.marksFailed ?? false,
    expanded: preset.expanded ?? [],
    filter: preset.filter ?? "all",
    inviteDraft: preset.inviteDraft ?? false,
    adding: preset.adding ?? false,
    next,
  };
}

/* ------------------------------------------------------------------ */
/* The boxes a visit brings                                            */
/* ------------------------------------------------------------------ */

/** Access, whose detail points at the access Task drawn under it, if any. It shows its face: never Open (O10). */
function accessFact(person: SpecimenPerson, store: Store, work: Box | null): Box {
  return box<SpecimenPerson>({
    id: `access-${person.id}`,
    kind: "fact",
    job: "Whether this person can sign in, and how.",
    label: "Identity and access",
    owner: IMAM,
    fold: "choice",
    read: store.model.accessFailed
      ? {
          status: "failed",
          sentence: "Couldn't read the sign-in state",
          detail: "Nothing about access is inferred from the failed read.",
          retry: () => store.apply((model) => ({ ...model, accessFailed: false })),
        }
      : ready(person),
    prose: (value) => (value.account === "Active" ? "LMS access is active." : "No LMS account yet. There is no access here to change."),
    detail: (value) =>
      value.account === "Active"
        ? "Their membership here decides what they can open."
        : work?.id.startsWith("invite-")
          ? "No invitation yet. Invite them below."
          : work
            ? "Their invitation is below."
            : "Their invitation was sent.",
  });
}

function resendResult(outcome: ResendOutcome, person: SpecimenPerson, apply: Store["apply"]): Result {
  const confirmed: Result = { state: "confirmed", cap: `Sent a setup email to ${first(person.name)}`, at: "6:31 PM", effects: [`Sent a fresh setup email to ${person.name}`], remains: ["Their invitation now reads Invitation sent"] };
  switch (outcome) {
    case "confirmed":
      return confirmed;
    case "refused":
      return { state: "refused", cap: "Not sent.", sentence: "The mail provider refused the address on this invitation.", draft: "kept" };
    case "unknown":
      return {
        state: "unknown",
        cap: "Sending the setup email",
        requestId: "specimen-resend",
        sentence: "We could not confirm whether the email was sent. Check before sending again.",
        check: {
          verb: "Check",
          note: "Reads the invitation. Nothing is sent again.",
          run: async () => {
            apply((model) => ({ ...model, resend: "confirmed", invitations: { ...model.invitations, [person.id]: SENT } }));
            return { ...confirmed, cap: "Checked: the email was sent" };
          },
        },
      };
    default:
      return { state: "none" };
  }
}

function resendTask(person: SpecimenPerson, invitation: NonNullable<InvitationState>, store: Store): Box {
  const { model, apply } = store;
  const result = resendResult(model.resend, person, apply);
  return box<SpecimenPerson>({
    id: `resend-${person.id}`,
    kind: "task",
    job: "Send a fresh setup email on this person's invitation.",
    label: "Send a fresh setup email",
    owner: IMAM,
    fold: "choice",
    read: ready(person),
    detail: () => "Their invitation is the record. Sending again changes nothing else.",
    state: () =>
      model.resend === "confirmed"
        ? { class: "done", word: "Sent" }
        : model.resend === "refused"
          ? { class: "failed", word: "Not sent" }
          : model.resend === "unknown"
            ? { class: "reading", word: "Not confirmed" }
            : { class: "draft", word: "Ready to send" },
    act: { does: "open", verb: "Send a fresh setup email" },
    candidate: { eligible: model.resend !== "confirmed", blocked: null, rank: 1 },
    parts: [{ id: "invitation", label: "Invitation", state: { class: invitation.class, word: invitation.word }, owner: IMAM, fold: "never", draft: "none", summary: "The invitation's own record owns history, resend and revoke." }],
    commit: {
      verb: "Send setup email",
      cap: "When you send",
      effects: () => [{ id: "send", sentence: `Send a fresh setup email to ${person.name}, to the address on their invitation` }],
      blockers: () => [],
      note: "Nobody is matched by email or name.",
      run: async () => {
        const outcome = model.next;
        apply((before) => ({ ...before, resend: outcome, invitations: outcome === "confirmed" ? { ...before.invitations, [person.id]: SENT } : before.invitations }));
        return resendResult(outcome, person, apply);
      },
    },
    result,
    draft: false,
  });
}

function inviteTask(person: SpecimenPerson, store: Store): Box {
  const draft = store.model.inviteDraft && person.id === "p6";
  return box<SpecimenPerson>({
    id: `invite-${person.id}`,
    kind: "task",
    job: "Invite this person to sign in with a managed account.",
    label: "Invite to sign in",
    owner: IMAM,
    fold: "choice",
    read: ready(person),
    detail: () => "The courses are theirs either way.",
    state: () => (draft ? { class: "draft", word: "Draft kept in this browser" } : { class: "quiet", word: "No sign-in yet" }),
    act: { does: "open", verb: draft ? "Continue the invitation" : "Invite to sign in" },
    candidate: { eligible: true, blocked: null, rank: 4 },
    parts: [
      {
        id: "account",
        label: "Account",
        state: { class: "waiting", word: "Needed" },
        owner: IMAM,
        fold: "never",
        draft: "browser",
        render: () => (
          <label className="grid gap-1 text-ui">
            Managed account
            <input className="h-[var(--control-height)] rounded-control border border-input bg-background px-3" defaultValue={draft ? `${first(person.name).toLowerCase()}-hakimi` : ""} />
          </label>
        ),
      },
    ],
    commit: {
      verb: "Send invitation",
      cap: "When you send",
      effects: () => [{ id: "create", sentence: `Create a managed account for ${first(person.name)} and send a setup email` }],
      blockers: () => [{ kind: "missing", part: "account", sentence: "Enter the managed account and a delivery email." }],
      run: async () => ({ state: "none" }),
    },
    result: { state: "none" },
    draft,
  });
}

function roleBoxes(person: SpecimenPerson, data: TenantPeople): Box[] {
  if (person.rel === "Student")
    return [
      box<SpecimenPerson>({
        id: `student-${person.id}`,
        kind: "fact",
        job: "This person's student record.",
        label: "Student record",
        owner: IMAM,
        fold: "choice",
        act: OPEN,
        read: ready(person),
        prose: () => `${data.program} · ${data.term}`,
        detail: (value) => value.sid ?? "",
      }),
      box<string[]>({
        id: `academic-${person.id}`,
        kind: "list",
        job: "The courses this person is enrolled in, each with its marks and attendance.",
        label: "Academic record",
        owner: IMAM,
        fold: "choice",
        act: OPEN,
        read: ready([...data.courses]),
        detail: () => "Each course opens its marks and attendance.",
        state: (courses) => ({ class: "quiet", word: `${courses.length} courses` }),
        rows: (courses) => courses,
        row: {
          key: (course) => `c${data.courses.indexOf(course)}`,
          title: (course) => course,
          state: () => ({ class: "quiet", word: "Enrolled" }),
          act: (course) => ({ does: "visit", verb: "Marks and attendance", visit: `enroll:${person.id}/c${data.courses.indexOf(course)}` }),
        },
      } as ListBox<string[], string>),
    ];
  if (person.rel === "Faculty")
    return [
      box<string[]>({
        id: `assign-${person.id}`,
        kind: "list",
        job: "The sections this person teaches.",
        label: "Teaching assignments",
        owner: IMAM,
        fold: "choice",
        act: OPEN,
        read: ready([data.courses[0]]),
        detail: () => "Across every term",
        state: () => ({ class: "quiet", word: "1 section" }),
        rows: (courses) => courses,
        row: { key: (course) => course, title: (course) => course, state: () => ({ class: "quiet", word: "Instructor" }) },
      } as ListBox<string[], string>),
    ];
  return [];
}

function accessWork(person: SpecimenPerson, store: Store): Box | null {
  if (person.account === "Active") return null;
  const invitation = store.model.invitations[person.id];
  if (invitation && (invitation.class === "waiting" || store.model.resend !== "none")) return resendTask(person, invitation, store);
  return invitation ? null : inviteTask(person, store);
}

function usePersonVisit(level: VisitLevel | null): VisitResolution | null {
  const store = useContext(SpecimenStore);
  if (!level || !store) return null;
  const person = store.data.people.find((candidate) => candidate.id === level.id);
  if (!person) return { status: "refused" };
  const subject = `${person.name} · ${person.rel}`;
  const work = accessWork(person, store);
  if (level.job === "access")
    return { status: "ready", subject, boxes: [accessFact(person, store, work), ...(work ? [work] : [])], opens: work ? [work.id] : [] };
  const boxes = [accessFact(person, store, work), ...(work ? [work] : []), ...roleBoxes(person, store.data)];
  return { status: "ready", subject, boxes, opens: store.model.expanded.map((kind) => `${kind}-${person.id}`) };
}

function useEnrollVisit(level: VisitLevel | null): VisitResolution | null {
  const store = useContext(SpecimenStore);
  if (!level || !store) return null;
  const { data, model, apply } = store;
  const person = data.people.find((candidate) => candidate.id === level.id && candidate.rel === "Student");
  const course = level.job === "c0" || level.job === "c1" ? data.courses[Number(level.job.slice(1))] : undefined;
  if (!person || !course) return { status: "refused" };
  const owner: FactOwner = data.marksOwner === "classroom" ? { system: "classroom", readAt: READ_AT } : IMAM;
  // One read feeds both boxes; each says its own failure, and Try again reads it again (R2, CASE-025).
  const retry = () => apply((before) => ({ ...before, marksFailed: false }));
  const gradeFailed = { status: "failed", sentence: "Couldn't read marks and attendance", detail: "No grade or attendance is drawn. This is not a missing mark.", retry } as const;
  const marksFailed = { status: "failed", sentence: "Couldn't read the marks", detail: "No mark is shown. This is not an empty list.", retry } as const;
  const marks = [
    { title: "Reading response", facts: "Due Sep 14", mark: "18 of 20" },
    { title: "Source summary", facts: "Due Sep 28", mark: "17 of 20" },
    { title: "Midterm", facts: "Due Oct 19", mark: null },
  ];
  type Mark = (typeof marks)[number];
  return {
    status: "ready",
    subject: `${first(person.name)} in ${short(course)}`,
    // A failed read rests on its face, where it says so and offers Try again.
    opens: model.marksFailed ? [] : [`marks-${person.id}-${level.job}`],
    boxes: [
      box<Mark[]>({
        id: `grade-${person.id}-${level.job}`,
        kind: "fact",
        job: "This learner's working grade and attendance in this course.",
        label: "Grade and attendance",
        owner,
        fold: "choice",
        read: model.marksFailed ? gradeFailed : ready(marks),
        figures: (rows) => [
          { value: 5, unit: "of 6 sessions attended" },
          { value: rows.filter((row) => row.mark).length, unit: `of ${rows.length} marked` },
        ],
        detail: () => "Working grade B+. Read-only here; marks are entered in the course.",
        ...(data.marksOwner === "classroom" ? { act: { does: "leave", verb: "Open in Classroom", providerUrl: "https://classroom.google.com/" } as const } : {}),
      }),
      box<Mark[]>({
        id: `marks-${person.id}-${level.job}`,
        kind: "list",
        job: "This learner's marks and sessions in this course.",
        label: "Marks and sessions",
        owner,
        fold: "choice",
        act: OPEN,
        read: model.marksFailed ? marksFailed : ready(marks),
        detail: () => "Each mark with its due date.",
        state: (rows) => ({ class: "quiet", word: `${rows.length} marks, 6 sessions` }),
        rows: (rows) => rows,
        row: {
          key: (row) => row.title,
          title: (row) => row.title,
          facts: (row) => row.facts,
          state: (row) => (row.mark ? { class: "done", word: row.mark } : { class: "waiting", word: "Not yet marked" }),
        },
      } as ListBox<Mark[], Mark>),
    ],
  };
}

const SPECIMEN_TARGETS: readonly VisitTarget[] = [
  { kind: "registered", object: "person", owner: "specimen", jobs: ["access"], noun: "Person", hosts: ["people"], useBoxes: usePersonVisit },
  { kind: "registered", object: "enroll", owner: "specimen", jobs: "record", noun: "Enrollment", hosts: ["people"], useBoxes: useEnrollVisit },
];

/* ------------------------------------------------------------------ */
/* The origin page: the register and one outside Task                  */
/* ------------------------------------------------------------------ */

function rowState(person: SpecimenPerson, model: Model) {
  if (model.inviteDraft && person.id === "p6") return { class: "draft", word: "Draft kept in this browser" } as const;
  const invitation = model.invitations[person.id];
  return invitation ?? ({ class: "quiet", word: person.account } as const);
}

function PeoplePage() {
  const { data, model } = useContext(SpecimenStore)!;
  const rows = model.filter === "no-account" ? data.people.filter((person) => person.account === "No LMS account") : data.people;
  const unsigned = data.people.filter((person) => person.rel === "Faculty" && person.account === "No LMS account").length;
  const register = box<SpecimenPerson[]>({
    id: "people",
    kind: "list",
    job: "Find a person once, then manage each record through its own authority.",
    label: "People",
    owner: IMAM,
    fold: "never",
    read: ready(rows),
    detail: (people) => `${people.length} people`,
    state: (people) => ({ class: "quiet", word: model.filter === "no-account" ? `${people.length} without an LMS account` : `${people.length} people` }),
    rows: (people) => people,
    find: { placeholder: "Find a person" },
    row: {
      key: (person) => person.id,
      title: (person) => person.name,
      facts: (person) => [person.rel, person.sid].filter(Boolean).join(" · "),
      state: (person) => rowState(person, model),
      act: (person) =>
        model.invitations[person.id]?.class === "waiting"
          ? { does: "visit", verb: "Open access", visit: `person:${person.id}/access` }
          : { does: "visit", verb: "Open person", visit: `person:${person.id}` },
    },
  } as ListBox<SpecimenPerson[], SpecimenPerson>);
  const addTeacher = box<null>({
    id: "add-teacher",
    kind: "task",
    job: "Add a teacher record without a sign-in.",
    label: "Add teacher",
    owner: IMAM,
    fold: "choice",
    read: ready(null),
    detail: () => "Creates a teacher record. A sign-in can come later.",
    state: () => ({ class: "quiet", word: unsigned === 0 ? "Every teacher has a sign-in" : `${unsigned} ${unsigned === 1 ? "teacher has" : "teachers have"} no sign-in` }),
    act: { does: "open", verb: "Add teacher" },
    candidate: { eligible: true, blocked: null, rank: 5 },
    parts: [
      {
        id: "name",
        label: "Name",
        state: { class: "waiting", word: "Needed" },
        owner: IMAM,
        fold: "never",
        draft: "browser",
        render: () => (
          <label className="grid gap-1 text-ui">
            Full name
            <input className="h-[var(--control-height)] rounded-control border border-input bg-background px-3" />
          </label>
        ),
      },
    ],
    commit: {
      verb: "Add teacher",
      cap: "When you add",
      effects: () => [{ id: "create", sentence: "Create a teacher record without a sign-in" }],
      blockers: () => [{ kind: "missing", part: "name", sentence: "Enter a name." }],
      run: async () => ({ state: "none" }),
    },
    result: { state: "none" },
    draft: false,
  });
  return (
    <div className="grid gap-4">
      <div>
        <h1 className="text-title">People</h1>
        <p className="text-ui text-muted-foreground">Find a person once, then manage each institutional record through its own authority.</p>
      </div>
      <Bento id="people" journey="people" boxes={[register, addTeacher]} defaultOpen={model.adding ? ["add-teacher"] : []} />
    </div>
  );
}

export function VisitView({ tenant, state, next = "confirmed" }: { tenant: TenantSlug; state: VisitState; next?: Model["next"] }) {
  const data = PEOPLE[tenant];
  const [model, apply] = useState(() => modelOf(data, state.preset, next));
  const store = useMemo(() => ({ data, model, apply }), [data, model]);
  return (
    <SpecimenStore.Provider value={store}>
      <VisitHost targets={SPECIMEN_TARGETS}>
        <PeoplePage />
      </VisitHost>
    </SpecimenStore.Provider>
  );
}

/* ------------------------------------------------------------------ */
/* The index, the focused view and the design system's section         */
/* ------------------------------------------------------------------ */

const OUTCOMES = ["confirmed", "refused", "unknown"] as const;

const href = (state: VisitState) => `/design-system?visits=${state.id}${state.visit ? `&visit=${state.visit}` : ""}`;

function VisitIndex({ current }: { current?: string }) {
  return (
    <nav aria-label="Visit specimens" className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-ui">
      <span className="font-semibold text-foreground">Visits</span>
      {VISIT_STATES.map((state) => (
        <Link key={state.id} to={href(state)} aria-current={state.id === current ? "page" : undefined} className={cn("text-primary hover:underline", state.id === current && "font-semibold text-foreground")}>
          {state.name}
        </Link>
      ))}
    </nav>
  );
}

/** One visit state alone, chosen by `?visits=`. */
export function VisitFocus({ tenant }: { tenant: TenantSlug }) {
  const [params] = useSearchParams();
  const state = VISIT_STATES.find((candidate) => candidate.id === params.get("visits")) ?? VISIT_STATES[0];
  const next = OUTCOMES.find((outcome) => outcome === params.get("outcome")) ?? "confirmed";
  return (
    <main className="min-h-screen bg-background px-4 py-6 text-foreground sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-[1200px] gap-6">
        <VisitIndex current={state.id} />
        <VisitView key={`${tenant}-${state.id}-${next}`} tenant={tenant} state={state} next={next} />
      </div>
    </main>
  );
}

/** The design system's Visits section: each state is its own page. */
export function VisitSection({ idPrefix }: { idPrefix: string }) {
  return (
    <section aria-labelledby={`${idPrefix}-visits`} className="space-y-4">
      <div>
        <h2 id={`${idPrefix}-visits`} className="text-tile-title">Visits</h2>
        <p className="text-ui text-muted-foreground">A box that leads to another record shows it in place and returns. Each state below is its own page.</p>
      </div>
      <VisitIndex />
    </section>
  );
}
